import React from "react";
import styled from "styled-components";

import { DEFAULT_EGG_TRANSFORM } from "../../constants";
import Button from "../Button";
import LightingEditor from "../LightingEditor";
import Panel from "../Panel";
import ToolPopover from "../ToolPopover";
import ToolSlider from "../ToolSlider";

// Author panel buttons are uniformly small + internal. Local wrapper so call
// sites don't have to repeat the props seven times. Spread order means
// callers can still override (e.g. Save → variant="primary").
const AuthorButton = (props) => (
  <Button size="small" variant="internal" {...props} />
);

function AuthorToolbar({ authoring, saveDisabled }) {
  const {
    marcVisible,
    setMarcVisible,
    marcOpacity,
    setMarcOpacity,
    marcTransformMode,
    setMarcTransformMode,
    eggTransform,
    setEggTransform,
    eggTransformMode,
    setEggTransformMode,
    resetMarcTransform,
    eggLights,
    setEggLights,
    resetEggLights,
    eggWarmth,
    setEggWarmth: onEggWarmthChange,
    eggRedshift,
    setEggRedshift: onEggRedshiftChange,
    save: onSave,
  } = authoring;

  const onToggleMarc = () => setMarcVisible((v) => !v);
  const onToggleMarcTransform = () => setMarcTransformMode((v) => !v);
  const onToggleEggTransform = () => setEggTransformMode((v) => !v);

  // Single-field updates: keeps the other eggTransform fields intact while
  // a slider drives one of them.
  const updateEgg = (field, value) =>
    setEggTransform({ ...eggTransform, [field]: value });
  const resetEggField = (field) =>
    setEggTransform({ ...eggTransform, [field]: DEFAULT_EGG_TRANSFORM[field] });
  const resetEggPosition = () =>
    setEggTransform({
      ...eggTransform,
      tx: DEFAULT_EGG_TRANSFORM.tx,
      ty: DEFAULT_EGG_TRANSFORM.ty,
    });

  const [openPopover, setOpenPopover] = React.useState(null);

  // Trigger button label: total intensity across all lights, so two lights
  // at 30 + 70 reads "Lights 100" (matches the visual brightness budget the
  // author is composing). The popover body itself is the shared
  // LightingEditor — it owns its own activeLight state.
  const totalIntensityPct = Math.round(
    (Array.isArray(eggLights) ? eggLights : []).reduce(
      (sum, L) => sum + (L?.intensity ?? 0),
      0,
    ) * 100,
  );

  const togglePopover = (key) => {
    setOpenPopover((p) => (p === key ? null : key));
  };

  // Radix only calls this with `false` (Escape, click-outside) since we drive
  // open via Anchor + caller state, not Trigger.
  const closeIfRequested = (open) => {
    if (!open) setOpenPopover(null);
  };

  return (
    <Panel
      style={{
        display: "flex",
        alignItems: "flex-start",
        flexDirection: "column",
        padding: "10px 16px",
        gap: "6px",
      }}
    >
      <Label>Author Mode</Label>

      <MiniRow>
        <ToolPopover
          open={openPopover === "marc-opacity"}
          onOpenChange={closeIfRequested}
          trigger={
            <AuthorButton
              type="button"
              $active={marcVisible}
              onClick={() => {
                if (marcVisible) {
                  togglePopover("marc-opacity");
                } else {
                  onToggleMarc();
                  setOpenPopover(null);
                }
              }}
              title="Toggle Marc onion (m) · click again to set opacity"
            >
              {marcVisible ? (
                <>
                  Marc <Num>{Math.round(marcOpacity * 100)}%</Num>
                </>
              ) : (
                "Show Marc"
              )}
            </AuthorButton>
          }
        >
          <PopLabel>Opacity · {Math.round(marcOpacity * 100)}%</PopLabel>
          <ToolSlider
            min={0}
            max={100}
            value={Math.round(marcOpacity * 100)}
            onValueChange={(v) => setMarcOpacity(v / 100)}
            ariaLabel="Marc opacity"
          />
          <MiniRow>
            <MiniButton type="button" onClick={onToggleMarc}>
              Hide Marc (m)
            </MiniButton>
          </MiniRow>
        </ToolPopover>

        <AuthorButton
          type="button"
          $active={marcTransformMode}
          onClick={onToggleMarcTransform}
          disabled={!marcVisible}
          title="Enable drag/rotate/scale on Marc"
        >
          Transform
        </AuthorButton>
        <AuthorButton
          type="button"
          $active={eggTransformMode}
          onClick={onToggleEggTransform}
          title="Drag the egg to reposition it"
        >
          Move Egg
        </AuthorButton>
      </MiniRow>

      <MiniRow>
        <ToolPopover
          open={openPopover === "egg-rot"}
          onOpenChange={closeIfRequested}
          trigger={
            <AuthorButton
              type="button"
              $active={openPopover === "egg-rot"}
              onClick={() => togglePopover("egg-rot")}
              title="Rotate egg"
            >
              Rot <Num>{formatDeg(eggTransform.rotation)}</Num>
            </AuthorButton>
          }
        >
          <PopLabel>Rotation · {formatDeg(eggTransform.rotation)}</PopLabel>
          <ToolSlider
            min={-90}
            max={90}
            value={eggTransform.rotation}
            onValueChange={(v) => updateEgg("rotation", v)}
            ariaLabel="Egg rotation"
          />
          <MiniRow>
            <MiniButton type="button" onClick={() => resetEggField("rotation")}>
              Reset
            </MiniButton>
            <MiniButton type="button" onClick={resetEggPosition}>
              Reset Position
            </MiniButton>
            <MiniButton type="button" onClick={resetMarcTransform}>
              Reset Marc
            </MiniButton>
          </MiniRow>
        </ToolPopover>

        <ToolPopover
          open={openPopover === "egg-scale"}
          onOpenChange={closeIfRequested}
          trigger={
            <AuthorButton
              type="button"
              $active={openPopover === "egg-scale"}
              onClick={() => togglePopover("egg-scale")}
              title="Scale egg"
            >
              Scale <Num>{formatPct(eggTransform.scale)}</Num>
            </AuthorButton>
          }
        >
          <PopLabel>Scale · {formatPct(eggTransform.scale)}</PopLabel>
          <ToolSlider
            min={50}
            max={200}
            value={Math.round((eggTransform.scale ?? 1) * 100)}
            onValueChange={(v) => updateEgg("scale", v / 100)}
            ariaLabel="Egg scale"
          />
          <MiniRow>
            <MiniButton type="button" onClick={() => resetEggField("scale")}>
              Reset
            </MiniButton>
          </MiniRow>
        </ToolPopover>

        <ToolPopover
          open={openPopover === "egg-scalex"}
          onOpenChange={closeIfRequested}
          trigger={
            <AuthorButton
              type="button"
              $active={openPopover === "egg-scalex"}
              onClick={() => togglePopover("egg-scalex")}
              title="Horizontal squish — makes the egg read more head-shaped"
            >
              Width <Num>{formatPct(eggTransform.scaleX)}</Num>
            </AuthorButton>
          }
        >
          <PopLabel>Width · {formatPct(eggTransform.scaleX)}</PopLabel>
          <ToolSlider
            min={50}
            max={150}
            value={Math.round((eggTransform.scaleX ?? 1) * 100)}
            onValueChange={(v) => updateEgg("scaleX", v / 100)}
            ariaLabel="Egg width"
          />
          <MiniRow>
            <MiniButton type="button" onClick={() => resetEggField("scaleX")}>
              Reset
            </MiniButton>
          </MiniRow>
        </ToolPopover>
      </MiniRow>

      <MiniRow>
        <ToolPopover
          open={openPopover === "egg-warmth"}
          onOpenChange={closeIfRequested}
          trigger={
            <AuthorButton
              type="button"
              $active={openPopover === "egg-warmth"}
              onClick={() => togglePopover("egg-warmth")}
              title="Warm tint applied to the egg photo (global, persisted to localStorage)"
            >
              Warmth <Num>{eggWarmth}</Num>
            </AuthorButton>
          }
        >
          <PopLabel>Warmth · {eggWarmth}</PopLabel>
          <ToolSlider
            min={0}
            max={100}
            value={eggWarmth}
            onValueChange={onEggWarmthChange}
            ariaLabel="Egg warmth"
          />
          <MiniRow>
            <MiniButton type="button" onClick={() => onEggWarmthChange(0)}>
              Reset
            </MiniButton>
          </MiniRow>
        </ToolPopover>

        <ToolPopover
          open={openPopover === "egg-redshift"}
          onOpenChange={closeIfRequested}
          trigger={
            <AuthorButton
              type="button"
              $active={openPopover === "egg-redshift"}
              onClick={() => togglePopover("egg-redshift")}
              title="Red bias on top of warmth — for marcs whose face reads flushed/red"
            >
              Red <Num>{eggRedshift}</Num>
            </AuthorButton>
          }
        >
          <PopLabel>Redshift · {eggRedshift}</PopLabel>
          <ToolSlider
            min={0}
            max={100}
            value={eggRedshift}
            onValueChange={onEggRedshiftChange}
            ariaLabel="Egg redshift"
          />
          <MiniRow>
            <MiniButton type="button" onClick={() => onEggRedshiftChange(0)}>
              Reset
            </MiniButton>
          </MiniRow>
        </ToolPopover>

        <ToolPopover
          open={openPopover === "egg-light"}
          onOpenChange={closeIfRequested}
          trigger={
            <AuthorButton
              type="button"
              $active={openPopover === "egg-light"}
              onClick={() => togglePopover("egg-light")}
              title="Per-marc lighting — stack multiple lights (key + fill, etc.). Each tab edits one light independently."
            >
              Lights <Num>{totalIntensityPct}</Num>
            </AuthorButton>
          }
        >
          <LightingEditor
            lights={Array.isArray(eggLights) ? eggLights : []}
            setLights={setEggLights}
            onResetAll={resetEggLights}
          />
        </ToolPopover>
      </MiniRow>

      <Button
        type="button"
        size="small"
        variant="primary"
        onClick={onSave}
        disabled={saveDisabled}
      >
        Save
      </Button>
    </Panel>
  );
}

function formatDeg(n) {
  return `${Math.round(n)}°`;
}

function formatPct(n) {
  return `${Math.round((n ?? 1) * 100)}%`;
}

const Label = styled.span`
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-warn);
`;

const Num = styled.span`
  display: inline-block;
  min-width: 40px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  opacity: 0.85;
`;

const PopLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-muted);
`;

const MiniRow = styled.div`
  display: flex;
  gap: 6px;
`;

const MiniButton = styled.button`
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  font-size: 11px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default AuthorToolbar;
